// Generated by OrchestKit Claude Plugin
// Created: 2026-09-10

/**
 * Correspondance statut → couleur, à un SEUL endroit.
 *
 * Les composants reçoivent un statut MÉTIER (`paye`, `credit`, `actif`…),
 * jamais une couleur. Deux raisons, et la seconde est la vraie :
 *
 * 1. Changer la couleur d'un statut se fait ici, pas dans douze écrans.
 * 2. Un même statut ne peut pas prendre deux couleurs selon la page. Un
 *    « payé » vert au tableau de bord et brun dans les ventes obligerait le
 *    lecteur à réapprendre le code à chaque écran — et sur un registre de
 *    comptes, la couleur EST l'information qu'on lit en premier.
 */

/** Familles de couleur disponibles, définies dans `tokens.css`. */
export type Famille = 'teal' | 'gold' | 'rust' | 'brick' | 'gray';

/**
 * Statuts métier de l'application.
 *
 * Cette liste est volontairement fermée : un statut inconnu doit provoquer une
 * erreur de typage à la compilation, pas s'afficher en gris par défaut. Un
 * badge gris silencieux masquerait un état que personne n'a pensé à traiter.
 */
export type Statut =
  // Ventes et règlements
  | 'paye'
  | 'partiel'
  | 'credit'
  | 'contrepassee'
  // Activité
  | 'actif'
  | 'inactif'
  // Créances
  | 'a_jour'
  | 'echu'
  // Traitements
  | 'en_attente'
  | 'valide'
  | 'rejete'
  | 'quarantaine';

interface DefinitionStatut {
  famille: Famille;
  libelle: string;
}

const STATUTS: Record<Statut, DefinitionStatut> = {
  // Le vert-brun signale ce qui est CLOS et sans reste : payé, validé, actif.
  paye: { famille: 'teal', libelle: 'Payé' },
  valide: { famille: 'teal', libelle: 'Validé' },
  actif: { famille: 'teal', libelle: 'Actif' },
  a_jour: { famille: 'teal', libelle: 'À jour' },

  // L'ocre signale ce qui reste OUVERT sans être en faute.
  credit: { famille: 'gold', libelle: 'Crédit' },
  en_attente: { famille: 'gold', libelle: 'En attente' },

  // Le rouille signale un état INTERMÉDIAIRE qui appelle une suite.
  partiel: { famille: 'rust', libelle: 'Partiel' },
  quarantaine: { famille: 'rust', libelle: 'Quarantaine' },

  // La brique signale ce qui a MAL tourné ou ce qui presse.
  echu: { famille: 'brick', libelle: 'Échu' },
  rejete: { famille: 'brick', libelle: 'Rejeté' },
  contrepassee: { famille: 'brick', libelle: 'Contrepassée' },

  // Le gris signale ce qui est HORS JEU, sans reproche.
  inactif: { famille: 'gray', libelle: 'Inactif' },
};

export function familleDe(statut: Statut): Famille {
  return STATUTS[statut].famille;
}

export function libelleDe(statut: Statut): string {
  return STATUTS[statut].libelle;
}

/**
 * Statut d'une vente d'après ce qu'elle doit encore.
 *
 * Placé ici plutôt que dans chaque écran : la même vente doit porter le même
 * badge au tableau de bord, dans la liste des ventes et sur la fiche client.
 */
export function statutVente(
  total: number,
  resteDu: number,
  contrepassee = false,
): Statut {
  if (contrepassee) return 'contrepassee';
  if (resteDu <= 0) return 'paye';
  return resteDu >= total ? 'credit' : 'partiel';
}

/**
 * Gravité d'une alerte de stock — RG-186.
 *
 * En rupture ou en négatif, c'est urgent. Sous le seuil sans être à zéro,
 * c'est à prévoir. Les traiter pareil ferait manquer les vraies ruptures dans
 * une liste d'avertissements.
 */
export function graviteStock(quantite: number): Famille {
  return quantite <= 0 ? 'brick' : 'gold';
}
